import { __decorate, __metadata } from "tslib";
import { I18nThemed } from 'interfaces/mixins';
import { theme } from '@dojo/framework/core/mixins/Themed';
import { v, w } from '@dojo/framework/core/vdom';
import { beforeProperties } from '@dojo/framework/core/decorators/beforeProperties';
import { logQuiz } from 'logger';
import { setQuestion, setInreview, setHasQueue, incrCursor } from './state';
import timesync from 'timesync';
import { QueueLoader, allqueues } from './queueloader';
import css from '../css/questionfeedback.m.css';
import { recordProgress } from './xapi';
let QuestionQueue = class QuestionQueue extends I18nThemed {
    instanceId;
    originalId;
    detaching = false;
    _inited = false;
    get loader() {
        const { instanceId, detaching } = this;
        const { svc, quizstore, info: { numberofquestions } } = this.properties;
        if (instanceId !== undefined) {
            if (allqueues.has(instanceId)) {
                return allqueues.get(instanceId);
            }
            if (!detaching) {
                const qloader = new QueueLoader({ svc, numberofquestions, instanceId, useQueue: true, quizstore });
                allqueues.set(instanceId, qloader);
                return qloader;
            }
        }
        return undefined;
    }
    get queue() {
        const { loader } = this;
        return loader && loader.queue;
    }
    stopLoading() {
        logQuiz.debug(() => `QuestionQueue#stopLoading for ${this.instanceId}`);
        const qloader = this.loader;
        if (qloader === undefined || qloader.stoppedloading) {
            return;
        }
        qloader.stopLoading();
    }
    has_next_question() {
        const { loader } = this;
        return Boolean(loader && !loader.queue.isEmpty());
    }
    next_question() {
        const { queue } = this;
        const { quizstore, quiz, info } = this.properties;
        if (queue) {
            const question = queue.dequeue();
            if (question) {
                incrCursor(quizstore);
                question.starttime = timesync.now().toJSON();
                setQuestion(quizstore)({ question });
                recordProgress(quiz, info, question.cursor);
            }
        }
    }
    async finish_quiz() {
        const { finalize, svc, quizstore } = this.properties;
        setQuestion(quizstore)({ question: undefined });
        setInreview(quizstore)({ inreview: false });
        await svc.finish_quiz();
        finalize();
    }
    update_results(results) {
        const { info: { qrid }, quiz: { id }, svc } = this.properties;
        results.forEach((result) => {
            result.qrlocator.qrid = qrid;
            result.qrlocator.quiz = id;
        });
        return svc.update_results({ results });
    }
    onDetach() {
        logQuiz.debug(() => `QuestionQueue#onDetach for ${this.originalId}`);
        this.detaching = true;
        const qloader = this.loader;
        const { instanceId } = this;
        qloader && qloader.stopLoading();
        instanceId && allqueues.delete(instanceId);
    }
    onAttach() {
        if (allqueues.size > 0) {
            logQuiz.debug(() => `already have QuestionQueue ${Array.from(allqueues.keys()).join(', ')}`);
            this.originalId = this.instanceId;
            this.instanceId = '--bad--';
            this.detaching = true;
            return;
        }
        const { quizstore } = this.properties;
        setHasQueue(quizstore)({ hasqueue: true });
        const { startquiz, instancekey } = this.properties;
        if (this.instanceId && this.instanceId !== instancekey) {
            logQuiz.debug(() => `QuestionQueue#onAttach for defunct ${this.originalId}`);
            return;
        }
        this.instanceId = instancekey;
        this.originalId = instancekey;
        logQuiz.debug(() => `QuestionQueue#onAttach for ${this.instanceId}`);
        const { loader } = this;
        console.log(`QuestionQueue#loader =`, loader);
        if (loader) {
            loader.load_questions(startquiz);
        }
    }
    render() {
        const { instancekey, svc, quizstore } = this.properties;
        const { detaching, instanceId } = this;
        if (detaching || (instanceId && instanceId !== instancekey)) {
            logQuiz.debug(() => `QuestionQueue#render defunct instance ${this.originalId}`);
            return null;
        }
        const { question } = this.properties;
        if (!question) {
            return v('div', { key: 'spinner', classes: [this.theme(css.loading)] }, [
                v('i', { classes: ['fa', 'fa-spinner', 'fa-spin', 'fa-lg'] })
            ]);
        }
        const { questiontype } = question;
        const key = `${questiontype}-${instancekey}`;
        return w(questiontype, {
            key,
            progressionSvc: svc,
            quizstore,
            hasNextQuestion: this.has_next_question,
            onNextQuestion: this.next_question,
            onFinish: this.finish_quiz,
            updater: this.update_results,
            loaded: false,
            filterOn: () => undefined,
            onReview: () => undefined,
            getQuestions: () => []
        });
    }
    stateProperties(properties) {
        const { quizstore } = properties;
        const { get, path } = quizstore;
        if (!this._inited) {
            const paths = [['quiz'], ['info'], ['question'], ['instancekey']];
            const invalidatePaths = paths.map((path) => quizstore.path(path.join('/')));
            const handle = quizstore.onChange(invalidatePaths, () => this.invalidate());
            this.own({
                destroy: () => {
                    handle.remove();
                }
            });
            this._inited = true;
        }
        properties.quiz = get(path('quiz'));
        properties.info = get(path('info'));
        properties.question = get(path('question'));
        properties.instancekey = get(path('instancekey'));
        return properties;
    }
};
__decorate([
    beforeProperties(),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object]),
    __metadata("design:returntype", Object)
], QuestionQueue.prototype, "stateProperties", null);
QuestionQueue = __decorate([
    theme(css)
], QuestionQueue);
export { QuestionQueue };
export default QuestionQueue;
